import React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DeleteButton, EditButton } from "@refinedev/mui";
import { IconButton, Tooltip } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { TableCell } from "@shared/components";
import {
  ActiveAgreementSwitch,
  ActiveBestSellerAgreementSwitch,
} from "@shared/components/SwitchsFields";
import { UseAgreementsListColumnsProps } from "./types";
import { useGenerateAgreementMutation } from "@modules/agreements/services/mutations";

export const useAgreementsListColumns = ({
  refetch,
  translate,
  handleGenerateToken,
  handleCloneAgreement,
  isGenerating,
}: UseAgreementsListColumnsProps): GridColDef[] => {

  const { mutate: generateToken } = useGenerateAgreementMutation();
  const [loadingETId, setLoadingETId] = React.useState<number | string | null>(
    null
  );

  return React.useMemo<GridColDef[]>(() => {
    const renderTextCell = (
      params: GridRenderCellParams,
      align: "left" | "center" | "right" = "left"
    ) => <TableCell.Text value={params.value} textAlign={align} />;

    const renderDateCell = (params: GridRenderCellParams) => (
      <TableCell.Date value={params.value} />
    );

    const renderLogoCell = (params: GridRenderCellParams) => (
      <TableCell.Container align="center">
        <img
          src={params.value}
          style={{
            width: "90%",
            height: "90%",
            objectFit: "fill",
          }}
        />
      </TableCell.Container>
    );

    const renderTokenCell = (params: GridRenderCellParams) => (
      <IconButton
        color="primary"
        onClick={() => handleGenerateToken?.(params.row.id)}
        title={translate("v1/agreement_admin.tooltips.view_token")}
        disabled={isGenerating}
      >
        <VisibilityIcon fontSize="medium" />
      </IconButton>
    );

    const renderETCell = (params: GridRenderCellParams) => (
      <LoadingButton
        variant="outlined"
        color="primary"
        size="small"
        loading={loadingETId === params.row.id}
        disabled={loadingETId !== null}
        onClick={() => {
          // A aba precisa ser aberta no gesto de clique, senão o navegador
          // bloqueia o popup. A URL real entra quando o token chega.
          const tab = window.open("about:blank", "_blank");

          setLoadingETId(params.row.id);
          generateToken(
            { id: params.row.id },
            {
              onSuccess: (data) => {
                const url = `https://dashboard.partiu.com.br/login/${data.token}`;

                if (tab && !tab.closed) {
                  tab.location.replace(url);
                } else {
                  window.open(url, "_blank");
                }
              },
              onError: () => tab?.close(),
              onSettled: () => setLoadingETId(null),
            }
          );
        }}
      >
        {translate("buttons.access")}
      </LoadingButton>
    );

    const renderActionsCell = (params: GridRenderCellParams) => (
      <TableCell.Container align="center">
        <Tooltip title={translate("v1/agreement_admin.buttons.clone")}>
          <IconButton
            color="primary"
            size="small"
            onClick={() =>
              handleCloneAgreement?.(params.row.id, params.row.name)
            }
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <EditButton hideText recordItemId={params.row.id} />
        <DeleteButton
          hideText
          resource="v1/agreement_admin"
          recordItemId={params.row.id}
          onSuccess={refetch}
        />
      </TableCell.Container>
    );

    return [
      {
        field: "id",
        headerName: translate("v1/agreement_admin.fields.id"),
        width: 80,
      },
      {
        field: "logo",
        headerName: translate("v1/agreement_admin.fields.logo"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: renderLogoCell,
      },
      {
        field: "name",
        headerName: translate("v1/agreement_admin.fields.name"),
        minWidth: 200,
        headerAlign: "left",
        renderCell: (params) => renderTextCell(params, "left"),
      },
      {
        field: "usages",
        headerName: translate("v1/agreement_admin.fields.usages"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: (params) => renderTextCell(params, "right"),
      },
      {
        field: "checkins",
        headerName: translate("v1/agreement_admin.fields.checkins"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: (params) => renderTextCell(params, "right"),
      },
      {
        field: "user_count",
        headerName: translate("v1/agreement_admin.fields.user_count"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: (params) => renderTextCell(params, "right"),
      },
      {
        field: "show_best_sellers",
        headerName: 'Ativar "Best Sellers"',
        minWidth: 200,
        headerAlign: "center",
        renderCell: (params) => (
          <TableCell.Container align="center">
            <ActiveBestSellerAgreementSwitch
              value={params.value}
              id={params.row.id}
            />
          </TableCell.Container>
        ),
      },
      {
        field: "active",
        headerName: translate("v1/agreement_admin.fields.active"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: (params) => (
          <TableCell.Container align="center">
            <ActiveAgreementSwitch value={params.value} id={params.row.id} />
          </TableCell.Container>
        ),
      },
      {
        field: "created_at",
        headerName: translate("v1/agreement_admin.fields.createdAt"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: renderDateCell,
      },
      {
        field: "updated_at",
        headerName: translate("v1/agreement_admin.fields.updatedAt"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: renderDateCell,
      },
      {
        field: "token",
        headerName: translate("v1/agreement_admin.fields.token"),
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: renderTokenCell,
      },
      {
        field: "et",
        headerName: translate("v1/agreement_admin.fields.et"),
        minWidth: 160,
        headerAlign: "center",
        align: "center",
        renderCell: renderETCell,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 160,
        sortable: false,
        renderCell: renderActionsCell,
      },
    ];
  }, [
    translate,
    refetch,
    handleGenerateToken,
    handleCloneAgreement,
    isGenerating,
    generateToken,
    loadingETId,
  ]);
};
